<script lang="ts" setup>
// Import styles.
import 'vidstack/player/styles/base.css';
// Register elements.
import 'vidstack/player';
import 'vidstack/player/ui';
import 'vidstack/icons';


import { isHLSProvider, type MediaCanPlayEvent, type MediaProviderChangeEvent } from 'vidstack';
import type { MediaPlayerElement } from 'vidstack/elements';
import { computed, onMounted, onBeforeUnmount, ref } from 'vue';

import VideoLayout from '@/front/components/player/components/layouts/VideoLayout.vue';

const props = defineProps({
  controls: {
    type: Boolean,
    default: true,
  },
  loop: {
    type: Boolean,
    default: false,
  },
  volume: {
    type: Number,
    default: 1,
  },
})


import { ClipStore } from '@/front/stores/clip_store.js';

const clipStore = ClipStore();
const $player = ref<MediaPlayerElement>();
const $src = ref('');


// Method to extract the video source from the video object
function getClipSource() {
  return clipStore.src
}
const emit = defineEmits(['ended'])

onMounted(() => {
  if (!clipStore.src) {
    log.info(3,"ClipPlayer onMounted no src")
    return
  }
  clipStore.player = $player

  // const { currentSrc, controlsHidden, currentTime, duration, fullscreen, playing, qualities, quality, seekableEnd, seekableStart, textTrack, volume, waiting, controlsVisible, ended, error, height, muted, paused, playbackRate, seeking, started, width } = $player.value?.state
  $src.value = getClipSource()

  $player.value?.subscribe(({ currentSrc, controlsHidden, fullscreen, playing, qualities, quality, seekableEnd, seekableStart, textTrack, volume, waiting, controlsVisible, duration, ended, error, height, muted, paused, playbackRate, seeking, started, width }) => {
    // Remove any text tracks that appear
    if (textTrack) {
      log.info(3, "ClipPlayer: Text track detected, removing all tracks", textTrack.label);
      removeAllTextTracks();
    }
    
    clipStore.setPlayerState({ currentSrc, controlsHidden, fullscreen, playing, qualities, quality, seekableEnd, seekableStart, textTrack, volume, waiting, controlsVisible, duration, ended, error, height, muted, paused, playbackRate, seeking, started, width })
  });
});

function onProviderChange(event: MediaProviderChangeEvent) {
  const provider = event.detail;
  // We can configure provider's here.
  if (isHLSProvider(provider)) {
    provider.config = {};
  }
}

function onEnded() {
  log.info(3,"ClipPlayer onEnded emitting ended")
  emit('ended')
}

  //

// We can listen for the `can-play` event to be notified when the player is ready.
function onCanPlay(event: MediaCanPlayEvent) {
  clipStore.startedAt = Date.now()
  log.info(1,"clipStore.requestedAt", clipStore.requestedAt)
  log.info(1,"clipStore.startedAt", clipStore.startedAt)
  log.info(1,"clipStore delay between requested and started", clipStore.startedAt - clipStore.requestedAt)
  // Aggressive text track removal for iOS
  removeAllTextTracks();
  
  $player.value?.play()
  // ...
}

onBeforeUnmount(() => {
  clipStore.clear()
})

function onTimeUpdate(event: MediaTimeUpdateEvent) {
  // log.info(3,'onTimeUpdate', event.detail.currentTime);
  // clipStore.handleTimeUpdate(event.detail.currentTime);
}

const getCrossOrigin = computed(() => {
  // log.info(3,"🌜 Player getCrossOrigin", $src.value)
  if ($src.value.includes("cloudfront")) {
    return false
  } else if ($src.value.includes("panopto")) {
    return 'anonymous'
  } else {
    return 'anonymous'
  }
});

const onClipClick = () => {
  log.info(3,"ClipPlayer onClipClick")
  return
  // emit('click')
}

function onPlay(event: MediaPlayEvent) {
  log.info(3,"onPlay", event)
  log.info(3,"props.volume", props.volume)
  const video = document.querySelector('#clip-player video')
  if (video) {
    video.volume = props.volume
    log.info(3,"document.querySelector('#clip-player video').volume", video.volume)
  }
  
  // Try removing tracks again on play for iOS
  removeAllTextTracks();
}

function removeAllTextTracks() {
  log.info(3, "ClipPlayer: Aggressively removing all text tracks");
  
  // Method 1: Remove via player API
  if ($player.value?.textTracks) {
    const tracks = [...$player.value.textTracks];
    for (const track of tracks) {
      track.mode = 'disabled';
      try {
        $player.value.textTracks.remove(track.id);
        log.info(3, "ClipPlayer: Removed track", track.label, track.language);
      } catch (error) {
        log.info(3, "ClipPlayer: Error removing track", error);
      }
    }
  }
  
  // Method 2: Direct DOM manipulation for iOS
  const video = document.querySelector('#clip-player video') as HTMLVideoElement;
  if (video?.textTracks) {
    for (let i = 0; i < video.textTracks.length; i++) {
      const track = video.textTracks[i];
      track.mode = 'disabled';
      log.info(3, "ClipPlayer: Disabled DOM track", track.label, track.language);
    }
  }
  
  // Method 3: Remove track elements from DOM
  const trackElements = document.querySelectorAll('#clip-player track');
  trackElements.forEach(track => {
    track.remove();
    log.info(3, "ClipPlayer: Removed track element from DOM");
  });
}

</script>

<template>
  <media-player
    class="w-full aspect-video bg-none text-white font-sans overflow-hidden ring-media-focus data-[focus]:ring-4"
    title="Sprite Fight"
    :src="$src"
    :crossOrigin="getCrossOrigin"
    :textTracks="[]"
    playsInline
    :loop="loop"
    @click.stop="onClipClick"
    @ended="onEnded"
    @provider-change="onProviderChange"
    @play="onPlay"
    @can-play="onCanPlay"
    @loaded-metadata="onLoadedMetadata"
    @time-update="onTimeUpdate"
    ref="$player"
    id="clip-player"
  >
    <media-provider id="clip-media-provider">
    </media-provider>
    <VideoLayout v-if="controls" :isClip="true" :controls="true" />
  </media-player>
</template>